import { createClient } from '@supabase/supabase-js'

// These will be your environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
const chatWebhookUrl = process.env.REACT_APP_N8N_CHAT_WEBHOOK_URL

// Validate URL format to catch dashboard URL mistakes
if (supabaseUrl && supabaseUrl.includes('/dashboard/')) {
  console.error('❌ INCORRECT SUPABASE URL FORMAT!')
  console.error('Current:', supabaseUrl)
  console.error('Should be: https://<project-ref>.supabase.co')
  throw new Error('Invalid Supabase URL - dashboard URL detected. Use API URL instead.')
}

// Check if environment variables are properly set
if (!supabaseUrl || !supabaseAnonKey ||
  supabaseUrl === 'your_supabase_url_here' ||
  supabaseAnonKey === 'your_supabase_anon_key_here') {
  console.warn('⚠️ Supabase environment variables not properly configured!')
  console.warn('Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local')
} else {
  console.log('✅ Supabase configuration loaded:', {
    url: supabaseUrl?.substring(0, 30) + '...',
    keyPrefix: supabaseAnonKey?.substring(0, 20) + '...'
  })
}

// Check chat webhook configuration
if (!chatWebhookUrl) {
  console.warn('⚠️ n8n Chat webhook URL not configured')
  console.warn('Please set REACT_APP_N8N_CHAT_WEBHOOK_URL in .env.local')
} else {
  console.log('✅ Chat webhook configured:', chatWebhookUrl?.substring(0, 50) + '...')
}

// Create Supabase client with explicit session persistence settings
// This ensures tokens are properly refreshed and sessions persist for 1 week
// Shared fetch wrapper: injects the x-session-id header on Database (REST) and
// Storage requests so session-based RLS works. Auth (/auth/v1/) and Edge
// Functions (/functions/v1/) are passed through UNTOUCHED.
// Used by BOTH clients below so the public client behaves identically.
const sessionAwareFetch = async (url, options = {}) => {
  // 🔥 SECURITY & CORS FIX: Only touch Database (REST) and Storage requests
  const isDatabaseRequest = url.includes('/rest/v1/');
  const isStorageRequest = url.includes('/storage/v1/');

  if (!isDatabaseRequest && !isStorageRequest) {
    // FAST PATH: Return original fetch for everything else (Auth, Functions, etc.)
    return fetch(url, options);
  }

  // Try to get session ID from multiple sources
  let sessionId = localStorage.getItem('wonder_session_id');

  // If no session_id in localStorage, check for FormFollowUp flow (gsheet_* tokens)
  if (!sessionId) {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get('session_token');
    if (sessionToken && sessionToken.startsWith('gsheet_')) {
      sessionId = sessionToken;
    }
  }

  // Inject x-session-id only if we have one
  if (sessionId) {
    const headers = new Headers(options.headers || {});
    headers.set('x-session-id', sessionId);
    return fetch(url, { ...options, headers });
  }

  // Passthrough if no session ID
  return fetch(url, options);
};

// Authenticated client — used by the admin dashboard (magic-link / MFA sessions).
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
  global: {
    fetch: sessionAwareFetch
  }
});

// Dedicated ANONYMOUS client for the public consultation flow.
// It never loads, persists, or refreshes an auth session, so even when a staff
// member (super_admin / pharmacist / coordinator) is logged into the admin
// dashboard in the SAME browser, customer-facing uploads always run as the
// `anon` role — no Authorization: Bearer staff-JWT is attached. This is what
// lets ANY visitor (anonymous customer OR logged-in staff) upload images,
// while the session-based RLS (x-session-id) still scopes each user to their
// own records. The admin dashboard keeps using `supabase` above.
export const supabasePublic = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
    storageKey: 'wonder_public_noauth', // isolate from the admin client's session
  },
  global: {
    fetch: sessionAwareFetch
  }
});

// ============================================
// AUTH STATE CHANGE LISTENER
// ============================================
// This listener handles automatic session refresh and auth state synchronization
supabase.auth.onAuthStateChange((event, session) => {
  switch (event) {
    case 'SIGNED_IN':
      console.log('✅ Auth: User signed in:', session?.user?.id);
      // Sync with admin auth status
      if (localStorage.getItem('admin_authenticated') !== 'true') {
        localStorage.setItem('admin_authenticated', 'true');
      }
      break;

    case 'SIGNED_OUT':
      console.log('👋 Auth: User signed out');
      // Clear all admin auth status and MFA trust
      localStorage.removeItem('admin_authenticated');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_session_id');
      localStorage.removeItem('admin_mfa_verified_session_id');
      localStorage.removeItem('admin_mfa_challenge_completed');
      localStorage.removeItem('admin_mfa_challenge_required');
      localStorage.removeItem('admin_mfa_factor_id');

      // 🧹 Also clear window/tab specific items just in case (though listener is cross-tab)
      sessionStorage.removeItem('admin_mfa_verified_session_id');
      sessionStorage.removeItem('admin_mfa_challenge_required');
      sessionStorage.removeItem('admin_mfa_factor_id');
      break;

    case 'TOKEN_REFRESHED':
      console.log('🔄 Auth: Token refreshed successfully');
      console.log('   Access token expires:', session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'unknown');
      break;

    case 'TOKEN_REFRESH_ERROR':
      console.error('❌ Auth: Token refresh failed - session may be expired');
      // A connection failure is not proof of revocation. The route can recheck;
      // only a confirmed sign-out/invalid session clears identity and MFA state.
      break;

    case 'INITIAL_SESSION':
      console.log('🎯 Auth: Initial session loaded');
      if (session) {
        console.log('   User:', session.user?.id);
        console.log('   Access token expires:', session.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'unknown');
        localStorage.setItem('admin_authenticated', 'true');
      }
      break;

    default:
      console.log('📝 Auth: Event:', event);
  }
});

// ============================================
// SESSION DEBUGGING UTILITIES
// ============================================

// Get current session info for debugging
export const getDebugSessionInfo = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      return { success: false, error: error.message };
    }

    if (!session) {
      return {
        success: true,
        isAuthenticated: false,
        message: 'No active session'
      };
    }

    const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
    const now = new Date();
    const expiresIn = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : null;

    return {
      success: true,
      isAuthenticated: true,
      userId: session.user?.id,
      email: session.user?.email,
      accessTokenExpiresAt: expiresAt?.toISOString(),
      accessTokenExpiresInSeconds: expiresIn,
      accessTokenExpired: expiresIn === 0,
      refreshTokenExists: !!session.refresh_token,
      localStorageAdminAuth: localStorage.getItem('admin_authenticated') === 'true',
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Manually trigger token refresh (for debugging)
export const manualRefreshToken = async () => {
  try {
    console.log('🔄 Manually refreshing token...');
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      console.error('❌ Token refresh failed:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Token refreshed successfully');
    return { success: true, session: data.session };
  } catch (error) {
    console.error('❌ Token refresh error:', error);
    return { success: false, error: error.message };
  }
};

// Check if session is valid and can be refreshed
export const isSessionValid = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error || !session) {
      return false;
    }

    // Check if token is expired or about to expire
    if (session.expires_at) {
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();
      const bufferTime = 60 * 1000; // 1 minute buffer

      if (expiresAt < new Date(now.getTime() + bufferTime)) {
        // Token is expired or about to expire, try to refresh
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          return false;
        }
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

// Utility function to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey &&
    supabaseUrl !== 'your_supabase_url_here' &&
    supabaseAnonKey !== 'your_supabase_anon_key_here')
}

// Session duration: 7 days in milliseconds
const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000

// Utility function to generate session ID
export const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
}

// Check if current session is expired
export const isSessionExpired = () => {
  const sessionTimestamp = localStorage.getItem('wonder_session_timestamp')
  if (!sessionTimestamp) return true

  const now = Date.now()
  const sessionAge = now - parseInt(sessionTimestamp)
  return sessionAge > SESSION_DURATION
}

// Get existing session ID (don't create new one)
export const getExistingSessionId = () => {
  if (isSessionExpired()) {
    clearSession()
    return null
  }
  return localStorage.getItem('wonder_session_id')
}

// Get or create session ID with 7-day expiration
export const getSessionId = () => {
  if (isSessionExpired()) {
    clearSession()
  }

  let sessionId = localStorage.getItem('wonder_session_id')
  if (!sessionId) {
    sessionId = generateSessionId()
    localStorage.setItem('wonder_session_id', sessionId)
    localStorage.setItem('wonder_session_timestamp', Date.now().toString())
    console.log('🆕 New session created:', sessionId)
  }
  return sessionId
}

// Clear expired session
export const clearSession = () => {
  localStorage.removeItem('wonder_session_id')
  localStorage.removeItem('wonder_session_timestamp')
  localStorage.removeItem('wonder_user_id')
  localStorage.removeItem('wonder_form_data')
  localStorage.removeItem('wonder_current_step')
  localStorage.removeItem('wonder_countdown_state')
  localStorage.removeItem('wonder_app_state')
  localStorage.removeItem('wonder_last_product_update')
  console.log('🧹 Session cleared (expired)')
}

// Clear session manually (for debugging)
export const clearSessionManually = () => {
  clearSession()
  console.log('🧹 Session manually cleared')
}

// Start a new consultation session - clears all localStorage and creates new session
// This preserves old consultation data in the database but starts fresh for the UI
export const startNewConsultationSession = () => {
  // Clear all localStorage keys related to consultation
  localStorage.removeItem('wonder_session_id')
  localStorage.removeItem('wonder_session_timestamp')
  localStorage.removeItem('wonder_user_id')
  localStorage.removeItem('wonder_form_data')
  localStorage.removeItem('wonder_current_step')
  localStorage.removeItem('wonder_countdown_state')
  localStorage.removeItem('wonder_app_state')
  localStorage.removeItem('wonder_last_product_update')

  // Generate and store new session ID
  const newSessionId = generateSessionId()
  localStorage.setItem('wonder_session_id', newSessionId)
  localStorage.setItem('wonder_session_timestamp', Date.now().toString())

  console.log('🔄 New consultation session started:', newSessionId)
  return newSessionId
}

// Get stored user ID
export const getStoredUserId = () => {
  if (isSessionExpired()) {
    clearSession()
    return null
  }
  return localStorage.getItem('wonder_user_id')
}

// Store user ID
export const storeUserId = (userId) => {
  localStorage.setItem('wonder_user_id', userId)
  console.log('💾 User ID stored:', userId)
}

// Step detection utilities for immediate restoration
export const getStoredFormData = () => {
  if (isSessionExpired()) {
    return null
  }
  try {
    const formData = localStorage.getItem('wonder_form_data')
    return formData ? JSON.parse(formData) : null
  } catch (error) {
    console.warn('Failed to parse stored form data:', error)
    return null
  }
}

export const storeFormData = (formData) => {
  localStorage.setItem('wonder_form_data', JSON.stringify(formData))
}

export const getStoredCurrentStep = () => {
  if (isSessionExpired()) {
    return 1
  }
  const step = localStorage.getItem('wonder_current_step')
  return step ? parseInt(step) : 1
}

export const storeCurrentStep = (step) => {
  localStorage.setItem('wonder_current_step', step.toString())
}

export const getStoredCountdownState = () => {
  if (isSessionExpired()) {
    return null
  }
  try {
    const countdownData = localStorage.getItem('wonder_countdown_state')
    if (!countdownData) return null

    const { timestamp, countdown, step } = JSON.parse(countdownData)
    const now = Date.now()
    const elapsed = Math.floor((now - timestamp) / 1000)
    const remaining = Math.max(0, countdown - elapsed)

    return { remaining, step, expired: remaining === 0 }
  } catch (error) {
    console.warn('Failed to parse countdown state:', error)
    return null
  }
}

export const storeCountdownState = (countdown, step) => {
  const countdownData = {
    timestamp: Date.now(),
    countdown,
    step
  }
  localStorage.setItem('wonder_countdown_state', JSON.stringify(countdownData))
}

export const clearCountdownState = () => {
  localStorage.removeItem('wonder_countdown_state')
}

// Store/retrieve advanced app states
export const getStoredAppState = () => {
  if (isSessionExpired()) {
    return null
  }
  try {
    const appState = localStorage.getItem('wonder_app_state')
    return appState ? JSON.parse(appState) : null
  } catch (error) {
    console.warn('Failed to parse stored app state:', error)
    return null
  }
}

export const storeAppState = (state) => {
  localStorage.setItem('wonder_app_state', JSON.stringify(state))
}

// Synchronous step detection for immediate rendering
export const detectInitialStep = () => {
  // Check if session is expired
  if (isSessionExpired()) {
    return 1
  }

  // Get stored app state first - this is crucial for advanced pages
  const appState = getStoredAppState()

  // If completion page should be shown, return step 3
  if (appState?.showCompletionPage) {
    console.log('🎯 Detected completion page state, returning step 3')
    return 3
  }

  // If we have products or routine showing, user is past analysis
  if (appState && (appState.showRoutine || appState.aiGeneratedProducts)) {
    console.log('🎯 Detected advanced state, staying in step 3+')
    return 3 // Stay in analysis/results step
  }

  // Get stored step and form data
  const storedStep = getStoredCurrentStep()
  const formData = getStoredFormData()
  const userId = getStoredUserId()

  // No user data = step 1
  if (!userId && !formData) {
    return 1
  }

  // Has basic form data = at least step 2, BUT only if image was successfully uploaded
  if (formData && (formData.name || formData.phone)) {
    // Check if image was successfully uploaded - don't allow bypassing this requirement
    const uploadSuccess = getStoredUploadSuccess()
    if (!uploadSuccess) {
      console.log('🚫 Form data exists but image upload not confirmed - resetting to step 1')
      return 1 // Force back to step 1 to re-upload image
    }
    // If stored step says 3 or higher, trust it
    return Math.max(2, storedStep)
  }

  // Has user ID but no form = step 2 ONLY if they uploaded an image
  if (userId) {
    const uploadSuccess = getStoredUploadSuccess()
    if (!uploadSuccess) {
      console.log('🚫 User ID exists but no image uploaded - resetting to step 1')
      return 1  // Force back to step 1 to upload image
    }
    return Math.max(2, storedStep)
  }

  return storedStep
}

// Product update timestamp utilities
export const getLastProductUpdate = () => {
  if (isSessionExpired()) {
    return null
  }
  return localStorage.getItem('wonder_last_product_update')
}

export const storeLastProductUpdate = (timestamp) => {
  localStorage.setItem('wonder_last_product_update', timestamp)
  console.log('💾 Last product update stored:', timestamp)
}

export const clearLastProductUpdate = () => {
  localStorage.removeItem('wonder_last_product_update')
  console.log('🧹 Last product update cleared')
}

// Upload success state management
export const setStoredUploadSuccess = (success) => {
  localStorage.setItem('wonder_upload_success', success ? 'true' : 'false')
  console.log('💾 Upload success stored:', success)
}

export const getStoredUploadSuccess = () => {
  const stored = localStorage.getItem('wonder_upload_success')
  return stored === 'true'
}

export const clearStoredUploadSuccess = () => {
  localStorage.removeItem('wonder_upload_success')
  console.log('🧹 Upload success cleared')
}

// Chat configuration utilities
export const getChatWebhookUrl = () => chatWebhookUrl
export const isChatConfigured = () => !!chatWebhookUrl
